'use client';
import {
    Popover,
    PopoverContent,
    PopoverHandler,
    Typography,
} from '@material-tailwind/react';
import { Suspense, useCallback, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Marquee from 'react-fast-marquee';

export default function LogosMarquee({ data }) {
    if (!data) {
        return <Suspense></Suspense>;
    }
    return (
        <div className="md:my-24">
            <p className="text-sm md:text-xl text-center text-gray-800">
                Join our 50+ Successful Implementation stories
            </p>
            <Marquee
                pauseOnHover
                direction="left"
                className="max-w-xs md:max-w-screen-2xl"
                speed={70}
            >
                <div className="flex items-center pt-12">
                    {data.map((item, index) => (
                        <ClientLogoItem
                            key={item.src}
                            logo={item.src}
                            data={item}
                            index={index}
                            priority={index < 4}
                        />
                    ))}
                </div>
            </Marquee>

            <div className="flex justify-end mt-2 md:mt-8">
                <Link
                    rel="canonical"
                    href="/company-details"
                    className="text-blue-600 font-medium flex items-center"
                >
                    view more <i className="ri-arrow-right-line ml-1"></i>
                </Link>
            </div>
        </div>
    );
}

function ClientLogoItem({ logo, data, priority }) {
    const [openPopover, setOpenPopover] = useState(false);

    const handleMouseEnter = useCallback(() => setOpenPopover(true), []);
    const handleMouseLeave = useCallback(() => setOpenPopover(false), []);

    const triggers = {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
    };

    return (
        <Popover
            open={openPopover}
            handler={setOpenPopover}
            className="outline-none"
        >
            <PopoverHandler
                {...triggers}
                className="outline-none mx-8 md:mx-14"
            >
                <a
                    href={data.link || '#'}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="px-12 flex items-center justify-center"
                    aria-label={`Visit ${data.name} website`}
                >
                    <Image
                        width={100}
                        height={45}
                        src={`/images/clients/${logo}`}
                        alt={`${data.name} logo`}
                        loading={priority ? 'eager' : 'lazy'}
                        sizes="100px"
                        style={{
                            objectFit: 'contain',
                            width: '100px',
                            height: 'auto',
                            maxHeight: '60px',
                        }}
                    />
                </a>
            </PopoverHandler>

            <PopoverContent {...triggers} className="z-50 max-w-[24rem]">
                <Typography
                    variant="h5"
                    color="blue-gray"
                    className="mb-2 flex items-center gap-2 font-medium text-base"
                >
                    {data.name}
                </Typography>

                <Typography
                    variant="small"
                    color="gray"
                    className="font-normal text-sm text-blue-gray-500 leading-relaxed"
                >
                    {data.clientDescription}
                </Typography>

                <div className="mt-6 flex items-center gap-8 border-t border-blue-gray-50 pt-4">
                    {data.location ? (
                        <Typography
                            variant="small"
                            color="gray"
                            className="flex items-center gap-2 text-sm font-normal text-blue-gray-500"
                        >
                            <i className="ri-map-pin-2-line"></i>
                            {data.location}
                        </Typography>
                    ) : null}

                    {data.link ? (
                        <Typography
                            as="a"
                            href={data.link}
                            target="_blank"
                            rel="noreferrer noopener"
                            variant="small"
                            color="gray"
                            className="outline-none flex items-center gap-2 text-sm font-normal text-blue-500 truncate max-w-[160px]"
                        >
                            <i className="ri-links-line shrink-0"></i>
                            {data.link
                                .replace('https://', '')
                                .replace('http://', '')}
                        </Typography>
                    ) : null}
                </div>
            </PopoverContent>
        </Popover>
    );
}
